// import * as Sentry from '@sentry/browser';
import { getLoadedBoard, ILevelConfig } from '../Config/Levels';
import { getRandom, IStateBoard } from '../State/reducers/boardHelpers'; // even with diff seed-random
import BoardObject from '../Tiles/BoardObject';
import Tile from '../Tiles/Tile';
import {
    FirecrackerBlacklist,
    ITileType,
    ShuffleBlacklist,
    SpecialMatchingSuits,
    Suits,
    TileSet,
} from '../Tiles/TileSet';
import BoardConnections from './BoardConnections';
import BoardUtils from './BoardUtils';

type TilePair = [Tile, Tile];

export type TBoard = Array<Tile | BoardObject>;

export interface IBoardMap {
    [keys: string]: Tile | BoardObject;
}

export interface IStacks {
    [key: string]: number;
}

const BoardArrangement = {
    fillBoard(tiles: Tile[], options: Partial<ILevelConfig>, objects: BoardObject[], seed: string) {
        const presets: TBoard = this.getPreset(tiles);
        const unfilled = this.getUnfilled(tiles);
        if (unfilled.length === 0) {
            return presets.sort((a, b) => a.layer - b.layer);
        }

        const vals = options.values;
        const pairs = options.pairsPerValue || 0;
        const fillSet = TileSet.initSet(vals, pairs);

        const filled =
            options.fillType === 'solvable'
                ? this.fillSolvable(tiles, fillSet, options.layerDiffs, objects, seed)
                : this.fillAllRandom(tiles, fillSet, options.layerDiffs, objects, seed);

        return ([] as Tile[]).concat(...filled).sort((a, b) => a.layer - b.layer);
    },

    fillSolvable(
        board: Tile[],
        set: ITileType[],
        dif: number,
        specials: BoardObject[],
        seed?: string,
        attempts: number = 0
    ): Tile[] {
        let unassigned = board.slice(0);
        let pairs: TilePair[] = [];
        let presets: Tile[] = [];
        let failed: boolean = false;
        while (unassigned.length > 0) {
            let available: Tile[] = this.filterObjectTiles(unassigned, specials);
            available = available.filter(x => !ShuffleBlacklist.includes(x.tileType.suit as Suits));

            const pre = this.filterPresetPairs(available);
            presets = [...presets, ...pre];
            available = available.filter(a => !pre.includes(a));
            unassigned = unassigned.filter(u => !pre.includes(u));

            available = this.getUnfilled(available);

            if (available.length < 2) {
                failed = true;
                break;
            }

            const availablePairs: TilePair[] = this.extractPairs(available, dif, seed); // odd typing not returned from inside literal
            unassigned = unassigned.filter(u => !availablePairs.find(pair => pair.includes(u)));
            pairs = [...pairs, ...availablePairs];
        }

        if (failed) {
            attempts++;
            if (attempts > 10) {
                pairs = this.fillPairs(pairs, set, seed);
                const randomSet = this.fillAllRandom(unassigned, set, dif, null, seed);
                return ([] as Tile[])
                    .concat(...pairs)
                    .concat(...randomSet)
                    .concat(...presets);
            }
            return this.fillSolvable(board, set, dif, specials, seed, attempts);
        }
        pairs = this.fillPairs(pairs, set, seed);

        return ([] as Tile[]).concat(...pairs).concat(...presets);
    },

    filterObjectTiles(tiles: TBoard, specials: BoardObject[]) {
        // remove any specials available
        const a = [...tiles, ...specials] as TBoard;
        const keys = BoardUtils.createKeys(a); // should be boardmap
        let available = a.filter(boardItem =>
            BoardConnections.checkAvailable(boardItem.locId, boardItem.blockPositions, keys)
        );
        const freeSpecials = available.filter(s => s.tileType.suit === 'special');
        available = available.filter(s => !freeSpecials.includes(s));
        specials = specials.filter(s => !freeSpecials.includes(s));

        const newKeys = BoardUtils.createKeys(available);
        let newAvailable = available.filter(boardItem =>
            BoardConnections.checkAvailable(boardItem.locId, boardItem.blockPositions, newKeys)
        );

        const newSpecialsAvailable = newAvailable.filter(t => t.tileType.suit === 'special');
        if (newSpecialsAvailable.length > 0) {
            const availableSpecials = newAvailable.filter(f => f.tileType.suit === 'special');
            newAvailable = newAvailable.filter(f => f.tileType.suit !== 'special');
            const newSpecials = [...specials, ...availableSpecials];
            return this.filterObjectTiles(newAvailable, newSpecials);
        }
        return newAvailable as Tile[];
    },

    fillAllRandom(
        board: Tile[],
        set: ITileType[],
        dif: number,
        specials: BoardObject[] = null,
        seed?: string
    ): Tile[] {
        const prefilled = this.getPreset(board) as Tile[];
        const unfilled = this.getUnfilled(board);
        const remaining =
            unfilled.length % 2 !== 0 ? board.slice(Math.floor(board.length / 2) * 2) : [];

        // taking just this out may fix missing gold
        // remaining.forEach(r => (r.tileType = TileSet.chooseFromSet(set)));
        let pairs = this.extractPairs(unfilled, dif, seed);
        pairs = this.fillPairs(pairs, set, seed);
        const result = ([] as Tile[])
            .concat(...pairs)
            .concat(...remaining)
            .concat(...prefilled);

        return result;
    },

    shuffleBoard(board: Tile[], specials: BoardObject[]) {
        let shuffleValues: ITileType[] = [];
        const allValues: ITileType[] = [];
        const match = (a: ITileType, b: ITileType) =>
            a.suit === b.suit &&
            (SpecialMatchingSuits.includes(b.suit as Suits) || a.value === b.value);

        const shuffled = board.reduce((acc, tileInfo) => {
            const { tileType } = tileInfo;
            const alreadyAdded = shuffleValues.find(t => match(t, tileType));
            if (!alreadyAdded) {
                shuffleValues.push(tileInfo.tileType);
            }
            allValues.push(tileInfo.tileType);
            const newInfo: Tile = {
                ...tileInfo,
                tileSprite: null,
                tileType: BoardUtils.convertTileData(''),
            };
            acc.push(newInfo);
            return acc;
        }, []);

        // for each in shuffled values
        shuffleValues = shuffleValues.reduce((acc, v) => {
            const vs = Array(allValues.filter(a => match(a, v)).length).fill(v);
            acc = [...acc, ...vs];
            return acc;
        }, []);

        return this.fillSolvable(shuffled, shuffleValues, 0, specials);
    },

    getFreeSpaces(state: IStateBoard, spacesNeeded: number): Array<[number, number, number]> {
        const [, currentOrigin] = state.boardSize;
        const sqrt = Math.ceil(Math.sqrt(spacesNeeded) / 2);
        let origin = [currentOrigin[0] - sqrt, currentOrigin[1] - sqrt];
        const originOffset = [
            origin[0] < 0 ? Math.abs(origin[0]) : 0,
            origin[1] < 0 ? Math.abs(origin[1]) : 0,
        ];
        origin = [origin[0] + originOffset[0], origin[1] + originOffset[1]];
        const max = [
            currentOrigin[0] + sqrt + originOffset[0],
            currentOrigin[1] + sqrt + originOffset[1],
        ];

        const places: Array<[number, number, number]> = [];

        for (let x = origin[0]; x <= max[0]; x++) {
            for (let y = origin[1]; y <= max[1]; y++) {
                places.push([0, x, y]);
            }
        }
        if (places.length < spacesNeeded) {
            // Sentry.captureMessage('not enough spaces for rearrange');
            return getLoadedBoard().tiles.map(x => x.position);
        }
        return places;
    },

    // used to remove preset tiles as pairs (to get available tiles) for solvable fill
    filterPresetPairs(available: Tile[]): Tile[] {
        const availablePresets = this.getPreset(available);
        const presetMatches = BoardUtils.findMatches(availablePresets);
        const presets: Tile[] = [];
        presetMatches.forEach(t => {
            presets.push(t);
        });

        return presets;
    },

    extractPairs(tiles: Tile[], dif: number, seed: string): TilePair[] {
        if (dif !== 0) {
            tiles.sort((a, b) => a.position[0] - b.position[0]);
        }
        const pairs: TilePair[] = [];
        while (tiles.length > 0) {
            if (tiles.length < 2) {
                return pairs;
            }
            const pair = Array(2) as TilePair;
            if (dif === 1) {
                pair[0] = tiles.shift();
                pair[1] = tiles.shift();
            } else if (dif === 2) {
                pair[0] = tiles.pop();
                pair[1] = tiles.shift();
            } else {
                pair[0] = tiles.splice(getRandom(0, tiles.length - 1, seed), 1)[0];
                pair[1] = tiles.splice(getRandom(0, tiles.length - 1, seed), 1)[0];
            }

            pairs.push(pair);
        }
        return pairs;
    },

    fillPairs(pairs: TilePair[], values: ITileType[], seed: string): TilePair[] {
        return pairs.map(pair => {
            let val: ITileType;

            // use gold first here to make sure it is not lost in random...
            //const gold = values.find((v) => v.suit === 'gold');
            // if(gold) {
            // val = gold;
            // values.splice(values.indexOf(gold), 1);
            // } else {
            // val = values.splice(getRandom(0, values.length - 1), 1)[0];
            // }

            val = values.splice(getRandom(0, values.length - 1, seed), 1)[0];
            // val = values[0];
            // delete again for pair
            values.splice(values.indexOf(val), 1);
            pair[0].tileType = val;
            pair[1].tileType = val;
            return pair;
        });
    },

    getPreset(tiles: TBoard): TBoard {
        return tiles.filter(t => t.tileType.suit !== 'notset');
    },

    getUnfilled(tiles: Tile[]): Tile[] {
        return tiles.filter(t => t.tileType.suit === 'notset');
    },

    getGold(tiles: TBoard): TBoard {
        return tiles.filter(t => t.tileType.suit === 'gold');
    },

    getPairsToRemove(
        tiles: TBoard,
        n: number,
        m: IBoardMap,
        onlyAvailable: boolean = true
    ): TBoard {
        let pairs: TBoard = [];
        let locTiles = tiles.slice(0);
        let available = onlyAvailable
            ? locTiles.filter(boardItem =>
                  BoardConnections.checkAvailable(
                      boardItem.locId,
                      boardItem.blockPositions,
                      Object.values(m).map(t => t.locId)
                  )
              )
            : locTiles.slice(0).sort((a, b) => b.layer - a.layer);

        let allMatched = false;
        while (pairs.length < n * 2 && available.length > 1 && !allMatched) {
            let matches = BoardUtils.findMatches(available.slice(0));
            matches = matches.filter(a => !FirecrackerBlacklist.includes(a.tileType.suit as Suits));
            if (matches.length === 0) {
                allMatched = true;
            }
            pairs = [...pairs, ...matches];
            if (pairs.length > n * 2) {
                pairs = pairs.slice(0, n * 2);
            }

            locTiles = locTiles.filter(a => !pairs.includes(a));

            const newMap = BoardUtils.createBoardMap(locTiles);
            available = locTiles.filter(boardItem =>
                BoardConnections.checkAvailable(
                    boardItem.locId,
                    boardItem.blockPositions,
                    Object.values(newMap).map(t => t.locId)
                )
            );
        }
        return pairs;
    },

    getAllOfMatchingType(id: string, tiles: TBoard, m: IBoardMap): string[] {
        const tileType = m[id].tileType;
        return tiles
            .filter(
                t =>
                    t.tileType.suit === tileType.suit &&
                    (SpecialMatchingSuits.includes(tileType.suit as Suits) ||
                        t.tileType.value === tileType.value)
            )
            .map(t => t.boardId);
    },
};

export default BoardArrangement;
